import { FolderOpen } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Media } from "@/services/bedrock";

import { isVideoMedia, QaStudioClip, QaStudioSource } from "./qaStudio";

export function clipAppearance(source: QaStudioSource) {
  if (source === "ORIGINAL") {
    return {
      label: "Original file",
      badge: "Orig",
      headerClass: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
      idleBorder: "border-slate-300 hover:border-slate-500 dark:border-slate-600 dark:hover:border-slate-400",
      badgeClass: "bg-slate-700 text-slate-100",
    };
  }
  return {
    label: "Edited video",
    badge: "Edit",
    headerClass: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
    idleBorder: "border-indigo-200 hover:border-indigo-400 dark:border-indigo-700 dark:hover:border-indigo-400",
    badgeClass: "bg-indigo-500 text-white",
  };
}

export function MediaStrip({
  clips,
  selectedKey,
  onSelect,
  highlighted,
}: {
  clips: QaStudioClip[];
  selectedKey?: string;
  onSelect: (clip: QaStudioClip) => void;
  highlighted?: boolean;
}) {
  const selectedRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [selectedKey]);

  return (
    <div
      className={cn(
        "shrink-0 border-t px-3 py-2",
        highlighted
          ? "border-amber-400 bg-amber-50 ring-2 ring-inset ring-amber-400 dark:bg-amber-950/40"
          : "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900"
      )}
    >
      <div className="flex min-w-0 items-center gap-2 overflow-x-auto">
        {clips.map((clip, index) => {
          const previous = clips[index - 1];
          return (
            <React.Fragment key={clip.key}>
              {previous && previous.source !== clip.source ? (
                <div className="mx-0.5 h-14 w-px shrink-0 bg-slate-200 dark:bg-slate-700" aria-hidden />
              ) : null}
              <StripThumb
                clip={clip}
                selected={clip.key === selectedKey}
                selectedRef={clip.key === selectedKey ? selectedRef : undefined}
                onSelect={onSelect}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function StripThumb({
  clip,
  selected,
  selectedRef,
  onSelect,
}: {
  clip: QaStudioClip;
  selected: boolean;
  selectedRef?: React.Ref<HTMLButtonElement>;
  onSelect: (clip: QaStudioClip) => void;
}) {
  const appearance = clipAppearance(clip.source);
  return (
    <button
      ref={selectedRef}
      type="button"
      title={appearance.label}
      className={cn(
        "relative h-16 w-16 shrink-0 overflow-hidden rounded-md border-2 bg-slate-100 dark:bg-slate-800",
        selected ? "border-indigo-400 ring-2 ring-indigo-400/40" : appearance.idleBorder
      )}
      onClick={() => onSelect(clip)}
    >
      <ClipThumb media={clip.media} />
      <span
        className={cn(
          "absolute inset-x-0 bottom-0 px-1 py-0.5 text-center text-[9px] font-semibold uppercase tracking-wide",
          appearance.badgeClass
        )}
      >
        {appearance.badge}
      </span>
    </button>
  );
}

function ClipThumb({ media }: { media: Media }) {
  if (media.thumbnailUri) {
    return <img src={media.thumbnailUri} alt="" className="h-full w-full object-cover" />;
  }
  if (isVideoMedia(media) && media.uri) {
    return (
      <video src={`${media.uri}#t=0.001`} className="h-full w-full object-cover" muted playsInline preload="metadata" />
    );
  }
  if (media.uri) {
    return <img src={media.uri} alt="" className="h-full w-full object-cover" />;
  }
  return <FolderOpen className="m-auto size-4 text-slate-500" />;
}
